import { fetchRequest } from 'src/commons/utils/async-request';
import { logger } from '../utils/logger';
import { LanguageSwitchMetricsPublisher } from '../utils/metrics';
import {
  NETWORK_ERROR_REGEX,
  REQUEST_ERROR_REGEX,
} from '../utils/async-request/constants';
import { SignInRedirectError } from '../errors';

const SUCCESS_COUNT_METRIC = 'languageSwitcherSuccessCount';
const FAILURE_COUNT_METRIC = 'languageSwitcherFailureCount';
const NETWORK_FAILURE_COUNT_METRIC = 'languageSwitcherNetworkErrorFailureCount';
const REQUEST_ABORTED_COUNT_METRIC =
  'languageSwitcherRequestAbortedFailureCount';
const UNAUTHENTICATED_COUNT_METRIC = 'unauthenticatedRequest';
const TIME_METRIC = 'time';

type GetErrorMetricReturnType =
  | typeof NETWORK_FAILURE_COUNT_METRIC
  | typeof REQUEST_ABORTED_COUNT_METRIC
  | typeof UNAUTHENTICATED_COUNT_METRIC
  | null;

const getErrorMetric = (error: any): GetErrorMetricReturnType => {
  if (NETWORK_ERROR_REGEX.test(error.message)) {
    return NETWORK_FAILURE_COUNT_METRIC;
  } else if (REQUEST_ERROR_REGEX.test(error.message)) {
    return REQUEST_ABORTED_COUNT_METRIC;
  } else if (error instanceof SignInRedirectError) {
    return UNAUTHENTICATED_COUNT_METRIC;
  }
  return null;
};

const postLanguageChange = async (
  currentLocale: string,
  newLocale: string,
): Promise<void> => {
  const startTime = new Date().getTime();
  const refTag = `ref=ams_langchange_${currentLocale}_${newLocale}`;
  const postURL = `/common/language/change?${refTag}`;

  const postData = {
    newLanguage: newLocale,
    oldLanguage: currentLocale,
  };

  try {
    const response = await fetchRequest(postURL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      // manually handle redirects.  See https://t.corp.amazon.com/V1114237403/communication
      redirect: 'manual',
      body: postData,
    });
    /**
     * https://t.corp.amazon.com/V1114237403/communication
     *
     * when `redirect: 'manual'` is set, fetch returns an opaque response
     * (empty response) and we are making the assumption that any redirection
     * is going to be the login screen.
     *  */
    if (response.type === 'opaqueredirect') {
      throw new SignInRedirectError('AJAX triggered redirect');
    }

    LanguageSwitchMetricsPublisher.publishCounterMonitor(
      SUCCESS_COUNT_METRIC,
      1,
    );
  } catch (error: any) {
    // todo: remove these metrics once dashboards have been updated because the metrics are captured in the `request` method
    logger.error(
      'Error occurred while switching languages in AACChrome',
      error,
    );
    LanguageSwitchMetricsPublisher.publishCounterMonitor(
      FAILURE_COUNT_METRIC,
      1,
    );

    const errorMetric = getErrorMetric(error);
    if (errorMetric !== null) {
      LanguageSwitchMetricsPublisher.publishCounterMonitor(errorMetric, 1);
    }
    throw error;
  } finally {
    LanguageSwitchMetricsPublisher.publishTimerMonitor(
      TIME_METRIC,
      new Date().getTime() - startTime,
    );
  }
};

export default postLanguageChange;
